import { useState } from "react";
import { motion } from "framer-motion";
import { Volume2, Mic, Square, Play, Star, Scale } from "lucide-react";
import { play, stopAudio } from "../../lib/audio";
import { useMic } from "../../lib/useMic";
import { Ruby } from "../lesson/Ruby";

const Round = ({ testid, onClick, disabled, className, children }) => (
  <motion.button data-testid={testid} onClick={onClick} disabled={disabled} whileTap={{ scale: 0.92 }}
    className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full flex flex-col items-center justify-center gap-1 shadow-lg text-base sm:text-lg disabled:opacity-50 ${className}`}>
    {children}
  </motion.button>
);

// Listen to the model voice, record yourself, play it back and compare.
export const SpeakPractice = ({ text, url, ruby, onRecorded, onDone, doneLabel = "我說好了", testid = "sp" }) => {
  const mic = useMic({ onRecorded });
  const [heard, setHeard] = useState(false);
  const listen = () => { setHeard(true); play(text, url); };
  const mine = () => { stopAudio(); new Audio(mic.clip).play().catch(() => {}); };
  const compare = () => {
    stopAudio();
    const a = new Audio(mic.clip);
    a.onended = () => setTimeout(() => play(text, url), 300);
    a.play().catch(() => play(text, url));
  };
  const busy = mic.state === "recording" || mic.state === "stopping";
  return (
    <div className="flex flex-col items-center gap-6" data-testid={`${testid}-practice`}>
      <button data-testid={`${testid}-model-btn`} onClick={listen} className="bg-white rounded-[2rem] border-4 border-[var(--sp-soft-b)] px-6 sm:px-8 py-4 flex items-center gap-3 shadow-md">
        <Volume2 className="text-[var(--sp)] shrink-0" size={30} />
        <Ruby data={ruby?.length ? ruby : [...text].map((c) => [c, ""])} className="text-3xl sm:text-5xl leading-[1.9] text-stone-900" testid={`${testid}-sentence`} />
      </button>
      <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
        <Round testid={`${testid}-listen-btn`} onClick={listen} disabled={busy} className="bg-white border-4 border-[var(--sp-soft-b)] text-[var(--sp-deep)]">
          <Volume2 size={30} /><span>{heard ? "再聽一次" : "聽標準音"}</span>
        </Round>
        {mic.state === "recording" ? (
          <Round testid={`${testid}-stop-btn`} onClick={mic.stop} className="bg-red-500 text-white animate-pulse"><Square size={30} fill="currentColor" /><span>說完了</span></Round>
        ) : (
          <Round testid={`${testid}-record-btn`} onClick={mic.start} disabled={mic.state === "stopping"} className="bg-[var(--sp)] text-white">
            <Mic size={34} /><span>{mic.state === "stopping" ? "聽聽看…" : mic.clip ? "重新錄音" : "換我說"}</span>
          </Round>
        )}
        {mic.clip && !busy && (<>
          <Round testid={`${testid}-mine-btn`} onClick={mine} className="bg-white border-4 border-amber-300 text-amber-700"><Play size={30} /><span>聽我說</span></Round>
          <Round testid={`${testid}-compare-btn`} onClick={compare} className="bg-amber-100 border-4 border-amber-300 text-amber-800"><Scale size={30} /><span>比一比</span></Round>
        </>)}
      </div>
      {mic.noMic && <p className="text-stone-500 text-lg" data-testid={`${testid}-no-mic`}>沒有麥克風也沒關係，跟著大聲說一次吧！</p>}
      {(mic.state === "done" || mic.noMic) && !busy && (
        <button data-testid={`${testid}-done-btn`} onClick={() => { stopAudio(); onDone(); }} className="kid-btn px-10 bg-amber-400 text-[var(--ink)] text-2xl flex items-center gap-2">
          <Star fill="currentColor" /> {doneLabel}
        </button>
      )}
    </div>
  );
};
